import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useParagonStore } from '@/stores/useParagonStore'
import { useState, useMemo } from 'react'

import {
  Search, Bell, RefreshCw, MoreHorizontal,
  ExternalLink, Zap, ArrowUpRight, ArrowDownRight,
} from 'lucide-react'


interface FleetTenant {
  tenant_id: string
  tenant_name: string
  slug: string
  plan: string
  status: string
  stripe_connected?: boolean
  user_count: number
  failed_webhooks_24h: number
  pending_dlq_items: number
  last_login: string | null
  health_status: 'healthy' | 'warning' | 'critical'
}

type FleetFilter = 'all' | 'healthy' | 'degraded' | 'incident' | 'maintenance'

const STATUS_DOT: Record<string, string> = {
  healthy:     'bg-[rgba(52,211,153,0.8)]',
  warning:     'bg-[rgba(251,191,36,0.8)]',
  degraded:    'bg-[rgba(251,191,36,0.8)]',
  critical:    'bg-[rgba(248,113,113,0.8)]',
  incident:    'bg-[rgba(248,113,113,0.8)]',
  maintenance: 'bg-white/30',
}

const PLAN_COLORS: Record<string, string> = {
  Paragon: 'text-[#C9A84C]',
  Pro:     'text-[rgba(99,102,241,0.7)]',
  Starter: 'text-white/30',
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  healthy: { label: 'Healthy', cls: 'text-[rgba(52,211,153,0.7)] bg-[rgba(52,211,153,0.06)] border-[rgba(52,211,153,0.1)]' },
  warning: { label: 'Degraded', cls: 'text-[rgba(251,191,36,0.7)] bg-[rgba(251,191,36,0.06)] border-[rgba(251,191,36,0.1)]' },
  critical: { label: 'Incident', cls: 'text-[rgba(248,113,113,0.7)] bg-[rgba(248,113,113,0.06)] border-[rgba(248,113,113,0.1)]' },
}

function FleetHeader({
  total,
  incidentCount,
  search,
  onSearch,
  onRefresh,
}: {
  total: number
  incidentCount: number
  search: string
  onSearch: (value: string) => void
  onRefresh: () => void
}) {
  return (
      <div className="h-14 flex items-center justify-between px-6 border-b border-black/[0.06] dark:border-white/[0.04] flex-shrink-0 bg-zinc-50 dark:bg-[#171717]">
      <div className="flex items-center gap-3">
        <span className="text-[14px] text-zinc-900 dark:text-zinc-50 font-medium">Fleet Health</span>
        <span className="text-[11px] font-mono text-zinc-400 dark:text-white/15">{total} tenants</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[rgba(52,211,153,0.7)] pulse-dot" />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onRefresh} aria-label="Refresh fleet health" className="p-2 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-zinc-400 dark:text-white/20 hover:text-zinc-600 dark:hover:text-white/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/40">
          <RefreshCw className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/[0.04] dark:bg-white/[0.03] border border-black/10 dark:border-white/[0.06]">
          <Search className="w-3.5 h-3.5 text-zinc-400 dark:text-white/20" />
          <input aria-label="Search tenants" type="text" value={search} onChange={e => onSearch(e.target.value)} placeholder="Search tenants…" className="bg-transparent text-[13px] text-zinc-900 dark:text-zinc-50 placeholder-zinc-400/50 dark:placeholder-white/15 outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] rounded w-48" />
        </div>
        <div className="relative">
          <button aria-label="View notifications" className="p-2 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-zinc-400 dark:text-white/20 hover:text-zinc-600 dark:hover:text-white/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/40">
            <Bell className="w-4 h-4" />
          </button>
          {incidentCount > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[rgba(248,113,113,0.8)]" />}
        </div>
      </div>
    </div>
  )
}

function FleetKpis({
  total,
  healthy,
  degraded,
  incident,
}: {
  total: number
  healthy: number
  degraded: number
  incident: number
}) {
  return (
    <div className="grid grid-cols-5 gap-px mb-5 rounded-xl overflow-hidden border border-black/[0.08] dark:border-white/[0.06]">
        {[
          { label: 'Fleet Uptime', value: '99.91%', change: '+0.02%', up: true, sub: 'Last 30 days', valueClass: 'text-zinc-900 dark:text-zinc-50' },
          { label: 'Healthy', value: String(healthy), change: `${total} total`, up: true, sub: 'All systems nominal', valueClass: 'text-zinc-900 dark:text-zinc-50' },
          { label: 'Degraded', value: String(degraded), change: degraded > 0 ? 'Webhook failures' : 'None', up: degraded === 0, sub: 'Warning threshold', valueClass: degraded > 0 ? 'text-[rgba(251,191,36,0.8)]' : 'text-zinc-900 dark:text-zinc-50' },
          { label: 'Incidents', value: String(incident), change: incident > 0 ? 'Needs attention' : 'All clear', up: incident === 0, sub: 'Critical status', valueClass: incident > 0 ? 'text-[rgba(248,113,113,0.8)]' : 'text-zinc-900 dark:text-zinc-50' },
          { label: 'Avg p95', value: '186ms', change: '-12ms', up: true, sub: 'Response time', valueClass: 'text-zinc-900 dark:text-zinc-50' },
        ].map((kpi) => (
        <div key={kpi.label} className="bg-black/[0.02] dark:bg-white/[0.02] p-4 hover:bg-black/[0.04] dark:hover:bg-white/[0.035] transition-colors">
          <div className="text-[11px] text-zinc-400 dark:text-white/25 mb-2">{kpi.label}</div>
          <div className={`text-[22px] font-mono font-light leading-none mb-1 ${kpi.valueClass}`}>{kpi.value}</div>
          <div className="flex items-center gap-1">
            {kpi.up ? <ArrowUpRight className="w-3 h-3 text-[rgba(52,211,153,0.6)]" /> : <ArrowDownRight className="w-3 h-3 text-[rgba(248,113,113,0.6)]" />}
            <span className={`text-[10px] font-mono ${kpi.up ? 'text-[rgba(52,211,153,0.6)]' : 'text-[rgba(248,113,113,0.6)]'}`}>{kpi.change}</span>
          </div>
          <div className="text-[10px] text-zinc-400 dark:text-white/15 mt-1 font-mono">{kpi.sub}</div>
        </div>
      ))}
    </div>
  )
}

function FleetFilterTabs({
  filter,
  total,
  healthy,
  degraded,
  incident,
  onChange,
}: {
  filter: FleetFilter
  total: number
  healthy: number
  degraded: number
  incident: number
  onChange: (value: FleetFilter) => void
}) {
  return (
    <div className="flex items-center gap-1 mb-4">
      {(['all', 'healthy', 'degraded', 'incident', 'maintenance'] as const).map((item) => {
        const cnt = item === 'all' ? total : item === 'healthy' ? healthy : item === 'degraded' ? degraded : item === 'incident' ? incident : 0
        return (
          <button key={item} onClick={() => onChange(item)} aria-pressed={filter === item} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] transition-[background-color,color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35 ${filter === item ? 'bg-black/[0.06] dark:bg-white/[0.06] text-zinc-900 dark:text-zinc-50' : 'text-zinc-400 dark:text-white/25 hover:bg-black/[0.04] dark:hover:bg-white/[0.03]'}`}>
            {item.charAt(0).toUpperCase() + item.slice(1)}
            <span className="text-[10px] font-mono text-zinc-400 dark:text-white/15">{cnt}</span>
          </button>
        )
      })}
    </div>
  )
}

function FleetTenantRow({
  tenant,
  expanded,
  onToggle,
  onImpersonate,
}: {
  tenant: FleetTenant
  expanded: boolean
  onToggle: () => void
  onImpersonate: (tenantId: string, tenantName: string) => void
}) {
  const isIncident = tenant.health_status === 'critical'
  const badge = STATUS_BADGE[tenant.health_status] || STATUS_BADGE.healthy
  const dotClass = STATUS_DOT[tenant.health_status] || 'bg-white/30'
  const sparkData = getMemoizedSpark(tenant.tenant_id)
  const planColor = PLAN_COLORS[tenant.plan] || 'text-white/30'

  return (
    <div className={`rounded-lg border transition-[background-color,color,border-color] duration-150 ${isIncident ? 'border-[rgba(248,113,113,0.15)] bg-[rgba(248,113,113,0.02)]' : 'border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02]'}`}>
      <button
        className="w-full flex items-center gap-0 px-4 py-3 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.01] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`fleet-row-panel-${tenant.tenant_id}`}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 mr-3 ${dotClass} ${isIncident ? 'pulse-dot' : ''}`} />
          <div className="w-[180px] flex-shrink-0">
            <div className="text-[13px] text-zinc-900 dark:text-zinc-50 truncate">{tenant.tenant_name}</div>
            <div className="text-[10px] font-mono text-zinc-400 dark:text-white/20">{tenant.slug}</div>
          </div>
        <div className="w-[90px] flex-shrink-0"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span></div>
        <div className="w-[70px] flex-shrink-0 text-right font-mono text-[12px] text-zinc-400 dark:text-white/40">-</div>
        <div className="w-[70px] flex-shrink-0 text-right font-mono text-[12px] text-zinc-400 dark:text-white/40">-</div>
        <div className={`w-[50px] flex-shrink-0 text-right font-mono text-[12px] ${tenant.failed_webhooks_24h > 0 ? 'text-[rgba(248,113,113,0.7)]' : 'text-zinc-400 dark:text-white/25'}`}>{tenant.failed_webhooks_24h}</div>
        <div className="w-[60px] flex-shrink-0 text-right font-mono text-[11px] text-zinc-400 dark:text-white/20">-</div>
        <div className={`w-[70px] flex-shrink-0 text-right font-mono text-[11px] ${planColor}`}>{tenant.plan || 'Starter'}</div>
        <div className="w-[80px] h-6 ml-3 flex-shrink-0">
          <SvgSparkline data={sparkData} stroke={isIncident ? 'rgba(248,113,113,0.5)' : 'rgba(201,168,76,0.4)'} fill={isIncident ? 'rgba(248,113,113,0.06)' : 'rgba(201,168,76,0.06)'} />
        </div>
        <MoreHorizontal className="ml-auto w-4 h-4 text-zinc-400 dark:text-white/15 flex-shrink-0" />
      </button>

      {expanded && (
        <div id={`fleet-row-panel-${tenant.tenant_id}`} className="px-4 pb-4 pt-1 border-t border-black/[0.05] dark:border-white/[0.04] ml-5">
          <div className="flex items-start gap-6 mb-3">
            {[
              { label: 'Clients', value: String(tenant.user_count ?? '-') },
              { label: 'Bookings 24h', value: tenant.pending_dlq_items ? String(tenant.pending_dlq_items) : '-' },
              { label: 'Last Deploy', value: '-' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-[10px] text-zinc-400 dark:text-white/20 mb-1">{label}</div>
                <div className="text-[14px] font-mono text-zinc-600 dark:text-white/50">{value}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] text-[#C9A84C] border border-[rgba(201,168,76,0.15)] hover:bg-[rgba(201,168,76,0.06)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"><ExternalLink className="w-3 h-3" /> Open Dashboard</button>
            <button onClick={() => onImpersonate(tenant.tenant_id, tenant.tenant_name)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] text-white/25 border border-white/[0.06] hover:bg-white/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Impersonate</button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] text-white/25 border border-white/[0.06] hover:bg-white/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"><Zap className="w-3 h-3" /> Force Deploy</button>
          </div>
        </div>
      )}
    </div>
  )
}

function FleetRows({
  isLoading,
  tenants,
  expandedRow,
  onToggle,
  onImpersonate,
}: {
  isLoading: boolean
  tenants: FleetTenant[]
  expandedRow: string | null
  onToggle: (tenantId: string) => void
  onImpersonate: (tenantId: string, tenantName: string) => void
}) {
  if (isLoading) return <div className="text-center text-zinc-400 dark:text-white/20 py-12 text-[13px] font-mono">Loading fleet data...</div>
  if (tenants.length === 0) return <div className="text-center text-zinc-400 dark:text-white/15 py-12 text-[13px]">No tenants match filter</div>

  return (
    <div className="space-y-1">
      {tenants.map((tenant) => (
        <FleetTenantRow
          key={tenant.tenant_id}
          tenant={tenant}
          expanded={expandedRow === tenant.tenant_id}
          onToggle={() => onToggle(tenant.tenant_id)}
          onImpersonate={onImpersonate}
        />
      ))}
    </div>
  )
}

function FleetColumnLegend() {
  return (
    <div className="flex items-center gap-0 px-4 py-2 mt-2 text-[10px] text-white/10">
      <span className="w-2 h-2 mr-3 flex-shrink-0" />
      <span className="w-[180px] flex-shrink-0">TENANT</span>
      <span className="w-[90px] flex-shrink-0">STATUS</span>
      <span className="w-[70px] flex-shrink-0 text-right">UPTIME</span>
      <span className="w-[70px] flex-shrink-0 text-right">P95</span>
      <span className="w-[50px] flex-shrink-0 text-right">ERRORS</span>
      <span className="w-[60px] flex-shrink-0 text-right">VERSION</span>
      <span className="w-[70px] flex-shrink-0 text-right">PLAN</span>
      <span className="w-[80px] ml-3 flex-shrink-0">24H</span>
    </div>
  )
}

function SvgSparkline({ data, stroke, fill }: { data: { t: number; v: number }[]; stroke: string; fill: string }) {
  const W = 80, H = 24
  const vals = data.map(d => d.v)
  const min = Math.min(...vals), max = Math.max(...vals)
  const range = max - min || 1
  const pts = vals.map((v, i) => ({
    x: (i / (vals.length - 1)) * W,
    y: H - ((v - min) / range) * (H - 2) - 1,
  }))
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const area = `${line} L${W},${H} L0,${H} Z`
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <path d={area} fill={fill} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1" />
    </svg>
  )
}

function mkSparkData() {
  return Array.from({ length: 24 }, (_, i) => ({ t: i, v: 40 + Math.sin(i / 3) * 20 + Math.random() * 15 }))
}

export default function ParagonFleetHealth() {
  const { startImpersonation } = useParagonStore()

  const [filter, setFilter] = useState<FleetFilter>('all')
  const [search, setSearch] = useState('')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const { data: fleet = [], isLoading, refetch } = useQuery({
    queryKey: ['fleet-health-matrix'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_fleet_health_matrix')
      if (error) throw error
      return (data ?? []) as unknown as FleetTenant[]
    },
    refetchInterval: 30000,
  })

  const counts = useMemo(() => ({
    healthy:  fleet.filter((t: FleetTenant) => t.health_status === 'healthy').length,
    degraded: fleet.filter((t: FleetTenant) => t.health_status === 'warning').length,
    incident: fleet.filter((t: FleetTenant) => t.health_status === 'critical').length,
  }), [fleet])

  const filtered = useMemo(() => {
    let result = fleet
    if (filter === 'healthy')     result = fleet.filter((t: FleetTenant) => t.health_status === 'healthy')
    else if (filter === 'degraded') result = fleet.filter((t: FleetTenant) => t.health_status === 'warning')
    else if (filter === 'incident') result = fleet.filter((t: FleetTenant) => t.health_status === 'critical')
    else if (filter === 'maintenance') result = []

    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter((t: FleetTenant) => t.tenant_name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q))
    }
    return result
  }, [fleet, filter, search])

  const handleImpersonate = async (tenantId: string, tenantName: string) => {
    try {
      const { data: session } = await supabase.auth.getSession()
      if (!session?.session?.access_token) return
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paragon-impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_tenant_id: tenantId }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Impersonation failed') }
      const { access_token } = await res.json()
      startImpersonation(access_token, tenantId, tenantName)
    } catch (err) {
      console.error('Impersonation failed:', err)
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-zinc-50 dark:bg-[#171717] transition-colors duration-200">
      <FleetHeader total={fleet.length} incidentCount={counts.incident} search={search} onSearch={setSearch} onRefresh={() => refetch()} />

      <div className="flex-1 overflow-y-auto p-6">
        <FleetKpis total={fleet.length} healthy={counts.healthy} degraded={counts.degraded} incident={counts.incident} />
        <FleetFilterTabs filter={filter} total={fleet.length} healthy={counts.healthy} degraded={counts.degraded} incident={counts.incident} onChange={setFilter} />
        <FleetRows isLoading={isLoading} tenants={filtered} expandedRow={expandedRow} onToggle={(tenantId) => setExpandedRow(prev => (prev === tenantId ? null : tenantId))} onImpersonate={handleImpersonate} />
        <FleetColumnLegend />
      </div>
    </div>
  )
}

// Stable spark data per tenant (memoized outside render to avoid re-generation)
const _sparkCache = new Map<string, { t: number; v: number }[]>()
function getMemoizedSpark(id: string) {
  if (!_sparkCache.has(id)) _sparkCache.set(id, mkSparkData())
  return _sparkCache.get(id)!
}
